import { useFirebase } from '@/firebase';
import { synchronizePublicAuthor } from '@/backend/firebase/publication-sync';
import { doc, updateDoc } from 'firebase/firestore';
import { useMemo } from 'react';
import type { CollaboratedNovel, User } from '@/lib/types';
import { useUserSession } from '@/context/user-session-context';

// Globally stable empty array reference to prevent render loops (no-any rule observed)
const EMPTY_ARRAY: never[] = [];

/**
 * Hook to fetch and ensure the existence of a user profile in Firestore.
 * Now reads from SubscriptionContext to avoid duplicate reads, while still 
 * providing the same interface.
 */
export function useUserProfile() {
  const { firestore, functions, user: authUser } = useFirebase();
  const { user: contextUser, loading: isContextLoading, error } = useUserSession();

  // 1. Create a reference to the user's Firestore document
  const userRef = useMemo(() => {
    if (!firestore || !authUser?.uid) return null;
    return doc(firestore, 'users', authUser.uid);
  }, [firestore, authUser?.uid]);

  // Process user data to ensure collaboratedNovels and pendingInvitations are stable arrays,
  // and userData is always defined as an object to avoid loop-causing default checks in components.
  const userData = useMemo(() => {
    const base: Partial<User> = contextUser || {};
    return {
      ...base,
      collaboratedNovels: (base.collaboratedNovels || EMPTY_ARRAY) as CollaboratedNovel[],
      pendingInvitations: base.pendingInvitations || EMPTY_ARRAY,
    } as User; // Add cast to ensure type consistency
  }, [contextUser]);

  const updateProfile = async (updates: Partial<User>) => {
    if (!userRef) return;
    await updateDoc(userRef, updates);
    if (updates.displayName !== undefined || updates.photoURL !== undefined) {
      await synchronizePublicAuthor(functions);
    }
  };

  return {
    userData,
    isLoading: isContextLoading,
    error,
    userId: authUser?.uid ?? null,
    docRef: userRef,
    updateProfile,
  };
}
